import React, { createContext, useState, useContext, ReactNode } from "react";

// Define available languages
export type Language = "en" | "fi" | "tr";

// Define translations structure
interface Translations {
  [key: string]: {
    [key: string]: string;
  };
}

// Define our translations
const translations: Translations = {
  en: {
    // Navbar
    home: "Home",
    features: "Features",
    pricing: "Pricing",
    contact: "Contact",
    tryDemo: "Try Demo",

    // Homepage
    heroTitle: "Modernize Your Restaurant Experience",
    heroDescription:
      "ByteBelly - A complete solution for restaurants to manage orders, deliveries, and customer engagement through a single platform. Available with monthly or yearly pricing.",
    getStarted: "Get Started",
    tryDemoButton: "Try Demo",

    // Features
    featuresHeroTitle: "ByteBelly: Powerful Features for Modern Restaurants",
    featuresHeroDescription:
      "Discover how ByteBelly's comprehensive suite of tools can streamline your restaurant operations and enhance the customer experience.",
    customerMobileApp: "Customer Mobile App",
    customerAppDescription:
      "Give your customers a seamless dining experience with ByteBelly's feature-rich mobile application.",
    intuitiveMenuBrowsing: "Intuitive Menu Browsing",
    intuitiveMenuBrowsingDesc:
      "Beautiful image-rich menus with detailed descriptions, ingredients, and dietary information.",
    securePaymentOptions: "Secure Payment Options",
    securePaymentOptionsDesc:
      "Multiple payment methods including credit cards and digital wallets for a smooth checkout experience.",
    realTimeOrderTracking: "Real-time Order Tracking",
    realTimeOrderTrackingDesc:
      "Follow delivery progress in real-time with accurate ETAs and order status updates.",
    specialDiscounts: "Special Discounts",
    specialDiscountsDesc:
      "Apply discount codes and view special offers on categories or individual products.",
    pushNotifications: "Push Notifications",
    pushNotificationsDesc:
      "Receive real-time updates about your order status, special promotions, and more.",
    lightDarkThemes: "Light & Dark Themes",
    lightDarkThemesDesc:
      "Choose between light and dark themes for comfortable browsing during day or night.",
    multiLanguageSupport: "Multi-language Support",
    multiLanguageSupportDesc:
      "Use the app in your preferred language with support for Turkish, Finnish, and English.",
    inAppMessaging: "In-app Messaging",
    inAppMessagingDesc:
      "Contact restaurant staff directly through the app for special requests or questions.",

    restaurantAdminDashboard: "Restaurant Admin Dashboard",
    restaurantAdminDashboardDesc:
      "ByteBelly's powerful mobile admin app to manage your restaurant operations efficiently and make data-driven decisions.",
    menuManagement: "Menu Management",
    menuManagementDesc:
      "Create and update menus, adjust pricing, and control item availability in real-time, all from your mobile device.",
    analyticsReporting: "Analytics & Reporting",
    analyticsReportingDesc:
      "Detailed insights into sales, popular items, peak hours, and customer preferences right on your mobile device.",
    orderManagement: "Order Management",
    orderManagementDesc:
      "Accept, prepare, and manage orders in real-time with instant notifications when new orders arrive.",
    productManagement: "Product Management",
    productManagementDesc:
      "Easily manage your inventory, add products, update prices, and organize categories from anywhere.",
    discountManagement: "Discount Management",
    discountManagementDesc:
      "Create and manage special offers, discount codes, and promotional pricing for categories or individual products.",
    realTimeNotifications: "Real-time Notifications",
    realTimeNotificationsDesc:
      "Get instant alerts for new orders, order status changes, and important business updates.",
    darkModeSupport: "Dark Mode Support",
    darkModeSupportDesc:
      "Enjoy eye-friendly dark mode when working late hours or in low-light environments.",
    multilingualInterface: "Multilingual Interface",
    multilingualInterfaceDesc:
      "Admin interface available in multiple languages: Turkish, Finnish, and English.",
    mobileOptimized: "Mobile Optimized",
    mobileOptimizedDesc:
      "Fully responsive admin interface designed specifically for smartphones and tablets.",

    readyToSeeByteBelly: "Ready to see ByteBelly in action?",
    checkoutLiveDemo:
      "Check out our live demo to experience ByteBelly's powerful features firsthand.",
    viewPricing: "View Pricing",

    // Features section
    allInOneTitle: "Your All-in-One Restaurant Solution",
    allInOneDescription:
      "ByteBelly helps you manage everything from menu items and orders to deliveries and customer engagement in one comprehensive platform.",

    // Features section
    mobileOrdering: "Mobile Ordering",
    mobileOrderingDesc:
      "Let customers browse your menu and place orders directly from their smartphones with our intuitive mobile app.",
    adminApp: "Admin Mobile App",
    adminAppDesc:
      "Manage your restaurant on the go with our dedicated mobile app for real-time order and product management.",
    orderTracking: "Order Tracking",
    orderTrackingDesc:
      "Real-time tracking of order status for both customers and restaurant staff with instant notifications.",
    discountSystem: "Discount System",
    discountSystemDesc:
      "Create and manage discount codes and special offers for categories or individual products to boost sales.",
    darkMode: "Light & Dark Mode",
    darkModeDesc:
      "Both customer and admin apps support light and dark themes for comfortable use day or night.",
    multilanguage: "Multi-language",
    multilanguageDesc:
      "Support for multiple languages including Turkish, Finnish, and English to serve diverse customers.",

    // Demo section
    tryLiveDemo: "Try ByteBelly Live Demo",
    liveDemoDesc:
      "Experience ByteBelly in action with our live demo. Explore the customer interface, try placing an order, and see how our platform can transform your restaurant business.",
    visitDemoSite: "Visit Demo Site",

    // CTA section
    transformRestaurant: "Ready to transform your restaurant?",
    joinRestaurants:
      "Join restaurants across Turkey and worldwide using ByteBelly to streamline their operations and delight customers.",
    contactUs: "Contact Us",

    // Footer
    quickLinks: "Quick Links",
    legal: "Legal",
    termsOfService: "Terms of Service",
    privacyPolicy: "Privacy Policy",
    cookiePolicy: "Cookie Policy",
    contactUsFooter: "Contact Us",
    allRightsReserved: "All rights reserved",

    // Pricing page
    pricingTitle: "ByteBelly Pricing",
    pricingDescription:
      "Choose the payment option that works best for your restaurant. All features included with no hidden fees.",
    monthly: "Monthly",
    yearly: "Yearly",
    save20Percent: "Save 20%",
    plan: "Plan",
    year: "year",
    month: "month",
    billedMonthly: "billed monthly",
    save: "Save",
    restaurantMobileAdmin: "Restaurant mobile admin app",
    customerMobileOrdering: "Customer mobile ordering app",
    orderTrackingSystem: "Order tracking system",
    categoryManagement: "Category management",
    discountCodes: "Discount codes & special offers",
    lightDarkTheme: "Light/dark theme support",
    unlimitedOrders: "Unlimited orders",
    unlimitedMenuItems: "Unlimited menu items",
    freeTechnicalSupport: "Free technical support",
    yearlyDescription:
      "Annual subscription with significant savings compared to monthly billing.",
    monthlyDescription:
      "Flexible monthly subscription with no long-term commitment.",
    frequentlyAskedQuestions: "Frequently Asked Questions",
    faqDescription:
      "Have questions? We're here to help. If you can't find what you're looking for, please contact us.",
    whatsIncludedQuestion: "What's included in both plans?",
    whatsIncludedAnswer:
      "Both the monthly and yearly plans include the same features: admin app, customer app, multi-language support, order management, and all other ByteBelly features.",
    demoQuestion: "Do you offer a demo?",
    demoAnswer: "Yes, you can check out our demo at",
    paymentMethodsQuestion: "What payment methods do you accept?",
    paymentMethodsAnswer:
      "We accept all major credit cards, including Visa, Mastercard, and American Express. Contact us for other payment methods.",
    cancelSubscriptionQuestion: "Can I cancel my subscription anytime?",
    cancelSubscriptionAnswer:
      "Yes, you can cancel your subscription at any time. Your access will continue until the end of your current billing period.",
    readyToRevolutionize: "Ready to revolutionize your restaurant?",
    contactSales: "Contact Sales",

    // Contact page
    getInTouch: "Get In Touch",
    contactHeroDescription:
      "Have questions about ByteBelly? We're here to help you find the perfect solution for your restaurant.",
    contactInformation: "Contact Information",
    contactTeamDescription:
      "Our team is ready to answer your questions and help you get started with ByteBelly restaurant management platform.",
    emailUs: "Email Us",
    callUs: "Call Us",
    businessHours: "Everyday, 24/7",
    visitUs: "Visit Us",
    ourWebsites: "Our Websites",
    demoLink: "Demo",
    sendUsMessage: "Send Us a Message",
    fullName: "Full Name",
    emailAddress: "Email Address",
    phoneNumber: "Phone Number",
    companyName: "Company Name",
    interestedIn: "Interested In",
    message: "Message",
    yourName: "Your name",
    emailPlaceholder: "your@email.com",
    phonePlaceholder: "(123) 456-7890",
    companyPlaceholder: "Your company",
    generalInquiry: "General Inquiry",
    requestDemo: "Request a Demo",
    pricingInfo: "Pricing Information",
    technicalSupport: "Technical Support",
    messagePlaceholder: "How can we help you?",
    sending: "Sending",
    sendMessage: "Send Message",
    thankYou: "Thank You!",
    messageSuccessText:
      "Your message has been sent successfully. We'll get back to you as soon as possible.",
    sendAnotherMessage: "Send Another Message",
    findUs: "Find Us",
    visitOurOffice:
      "Visit our office in Konya, Turkey. We'd love to meet you in person and discuss how ByteBelly can help your restaurant.",

    // Device showcase section
    experienceOnAllDevices: "Experience ByteBelly on All Devices",
    responsiveDesignDescription:
      "Our platform works seamlessly across desktop and mobile, giving you and your customers the flexibility to manage and order from anywhere.",
    availableOnAllPlatforms: "Available for iOS, Android and Web browsers",
    downloadApp: "Download App",

    // Powered By
    poweredBy: "Powered By",
    mobileAppDevelopment: "Mobile App Development",
    realtimeBackend: "Real-time Backend",
    cloudServices: "Cloud Services",
    scalableInfrastructure: "Scalable Infrastructure",
    trustedByRestaurants:
      "Trusted by 10+ restaurants worldwide with 100+ daily orders",

    // New translations
    footerCompanyDescription:
      "Revolutionizing the food delivery experience with our innovative mobile app and restaurant management solution.",
    officeLocation: "Konya, Turkey",
    firstMonthFree: "First month free with subscription",
    freeTrialTitle: "Try ByteBelly free for 30 days",
    freeTrialDescription:
      "Experience all ByteBelly features with no obligation or credit card required. Get full access to all features for 30 days.",
    startFreeTrial: "Start Free Trial",
    days: "days",
    fullAccess: "Full access to all features",
    noCommitment: "No commitment required",
    noCreditCard: "No credit card needed",
    cancelAnytime: "Cancel anytime",
    recommended: "RECOMMENDED",
    perfectFor: "Perfect for restaurants wanting to try before committing",
    noPaymentRequired: "No payment information required to start",
    contactViaWhatsApp: "Contact via WhatsApp",

    // New appointment booking translations
    bookAppointment: "Book an Appointment",
    scheduleDescription:
      "Schedule a meeting with our team to discuss your project.",
    videoConsultation: "Video Consultation",
    demoSession: "Product Demo",
    minutes30: "30 minutes",
    minutes45: "45 minutes",
    scheduleNow: "Schedule Now",
  },

  fi: {
    // Navbar
    home: "Etusivu",
    features: "Ominaisuudet",
    pricing: "Hinnoittelu",
    contact: "Yhteystiedot",
    tryDemo: "Kokeile demoa",

    // Homepage
    heroTitle: "Modernisoi Ravintolakokemuksesi",
    heroDescription:
      "ByteBelly - Täydellinen ratkaisu ravintoloille tilausten, toimitusten ja asiakassuhteiden hallintaan yhdellä alustalla. Saatavilla kuukausittaisella tai vuosittaisella hinnoittelulla.",
    getStarted: "Aloita",
    tryDemoButton: "Kokeile demoa",

    // Features
    featuresHeroTitle:
      "ByteBelly: Tehokkaita ominaisuuksia nykyaikaisille ravintoloille",
    featuresHeroDescription:
      "Tutustu, kuinka ByteBellyn kattava työkaluvalikoima voi tehostaa ravintolasi toimintaa ja parantaa asiakaskokemusta.",
    customerMobileApp: "Asiakkaiden mobiilisovellus",
    customerAppDescription:
      "Tarjoa asiakkaillesi saumaton ruokailukokemus ByteBellyn monipuolisella mobiilisovelluksella.",
    intuitiveMenuBrowsing: "Intuitiivinen ruokalistan selaaminen",
    intuitiveMenuBrowsingDesc:
      "Kauniit kuvarikkaat ruokalistat yksityiskohtaisilla kuvauksilla, ainesosilla ja ravintotiedoilla.",
    securePaymentOptions: "Turvalliset maksuvaihtoehdot",
    securePaymentOptionsDesc:
      "Useita maksutapoja, mukaan lukien luottokortit ja digitaaliset lompakot, sujuvaa maksuprosessia varten.",
    realTimeOrderTracking: "Reaaliaikainen tilausten seuranta",
    realTimeOrderTrackingDesc:
      "Seuraa toimituksen etenemistä reaaliajassa tarkkojen saapumisaikojen ja tilapäivitysten avulla.",
    specialDiscounts: "Erikoisalennukset",
    specialDiscountsDesc:
      "Käytä alennuskoodeja ja katso erikoistarjouksia kategorioittain tai yksittäisille tuotteille.",
    pushNotifications: "Push-ilmoitukset",
    pushNotificationsDesc:
      "Vastaanota reaaliaikaisia päivityksiä tilauksesi tilasta, erikoistarjouksista ja muusta.",
    lightDarkThemes: "Vaalea ja tumma teema",
    lightDarkThemesDesc:
      "Valitse vaalean ja tumman teeman välillä miellyttävää selaamista varten päivällä tai yöllä.",
    multiLanguageSupport: "Monikielinen tuki",
    multiLanguageSupportDesc:
      "Käytä sovellusta haluamallasi kielellä, tuettuja kieliä ovat turkki, suomi ja englanti.",
    inAppMessaging: "Sovelluksen sisäinen viestintä",
    inAppMessagingDesc:
      "Ota yhteyttä ravintolan henkilökuntaan suoraan sovelluksen kautta erityistoiveita tai kysymyksiä varten.",

    restaurantAdminDashboard: "Ravintolan hallintapaneeli",
    restaurantAdminDashboardDesc:
      "ByteBellyn tehokas mobiilihallintasovellus ravintolasi toimintojen hallintaan tehokkaasti ja dataperusteisten päätösten tekemiseen.",

    // Features section
    allInOneTitle: "Kaikki-yhdessä-ravintolaratkaisu",
    allInOneDescription:
      "ByteBelly auttaa sinua hallitsemaan kaikkea ruokalistoista ja tilauksista toimituksiin ja asiakassuhteisiin yhdellä kattavalla alustalla.",

    // Features section
    mobileOrdering: "Mobiilitilaus",
    mobileOrderingDesc:
      "Anna asiakkaiden selata ruokalistaasi ja tehdä tilauksia suoraan älypuhelimillaan intuitiivisella mobiilisovelluksellamme.",
    adminApp: "Ylläpitosovellus",
    adminAppDesc:
      "Hallitse ravintolaasi liikkeellä ollessasi omistautuneella mobiilisovelluksellamme reaaliaikaiseen tilausten ja tuotteiden hallintaan.",
    orderTracking: "Tilausten seuranta",
    orderTrackingDesc:
      "Seuraa tilausten tilaa reaaliajassa sekä asiakkaille että ravintolahenkilökunnalle välittömillä ilmoituksilla.",
    discountSystem: "Alennusjärjestelmä",
    discountSystemDesc:
      "Luo ja hallitse alennuskoodeja ja erikoistarjouksia kategorioittain tai yksittäisille tuotteille myynnin lisäämiseksi.",
    darkMode: "Vaalea & Tumma Tila",
    darkModeDesc:
      "Sekä asiakas- että ylläpitosovellukset tukevat vaaleaa ja tummaa teemaa miellyttävään käyttöön päivällä tai yöllä.",
    multilanguage: "Monikielisyys",
    multilanguageDesc:
      "Tuki useille kielille, mukaan lukien turkki, suomi ja englanti, erilaisten asiakkaiden palvelemiseksi.",

    // Demo section
    tryLiveDemo: "Kokeile ByteBelly-demoa",
    liveDemoDesc:
      "Koe ByteBelly käytännössä demoversion avulla. Tutustu käyttöliittymään, kokeile tilaamista ja näe, kuinka alustamme voi muuttaa ravintolatoimintaasi.",
    visitDemoSite: "Vieraile Demosivustolla",

    // CTA section
    transformRestaurant: "Valmiina muuttamaan ravintolasi toimintaa?",
    joinRestaurants:
      "Liity ravintoloihin ympäri Turkin ja maailman, jotka käyttävät ByteBellyä toimintojensa tehostamiseen ja asiakkaiden ilahduttamiseen.",
    viewPricing: "Katso hinnoitteluvaihtoehdot",
    contactUs: "Ota yhteyttä",

    // Footer
    quickLinks: "Pikalinkit",
    legal: "Lakiasiat",
    termsOfService: "Käyttöehdot",
    privacyPolicy: "Tietosuojakäytäntö",
    cookiePolicy: "Evästekäytäntö",
    contactUsFooter: "Ota yhteyttä",
    allRightsReserved: "Kaikki oikeudet pidätetään",

    // Pricing page
    pricingTitle: "ByteBelly Hinnoittelu",
    pricingDescription:
      "Valitse ravintolallesi parhaiten sopiva maksuvaihtoehto. Kaikki ominaisuudet sisältyvät ilman piilokuluja.",
    monthly: "Kuukausittain",
    yearly: "Vuosittain",
    save20Percent: "Säästä 20%",
    plan: "Suunnitelma",
    year: "vuosi",
    month: "kuukausi",
    billedMonthly: "laskutetaan kuukausittain",
    save: "Säästä",
    restaurantMobileAdmin: "Ravintolan mobiilihallintasovellus",
    customerMobileOrdering: "Asiakkaiden mobiilitilaussovellus",
    orderTrackingSystem: "Tilausten seurantajärjestelmä",
    categoryManagement: "Kategorioiden hallinta",
    discountCodes: "Alennuskoodit ja erikoistarjoukset",
    lightDarkTheme: "Vaalea/tumma teematuki",
    unlimitedOrders: "Rajoittamaton määrä tilauksia",
    unlimitedMenuItems: "Rajoittamaton määrä menun kohteita",
    freeTechnicalSupport: "Ilmainen tekninen tuki",
    yearlyDescription:
      "Vuositilaus merkittävillä säästöillä kuukausilaskutukseen verrattuna.",
    monthlyDescription:
      "Joustava kuukausitilaus ilman pitkäaikaista sitoutumista.",
    frequentlyAskedQuestions: "Usein Kysytyt Kysymykset",
    faqDescription:
      "Onko sinulla kysyttävää? Olemme täällä auttaaksemme. Jos et löydä etsimääsi, ota yhteyttä.",
    whatsIncludedQuestion: "Mitä sisältyy molempiin suunnitelmiin?",
    whatsIncludedAnswer:
      "Sekä kuukausi- että vuosisuunnitelmiin sisältyvät samat ominaisuudet: hallintasovellus, asiakassovellus, monikielinen tuki, tilausten hallinta ja kaikki muut ByteBellyn ominaisuudet.",
    demoQuestion: "Tarjoatteko demoa?",
    demoAnswer: "Kyllä, voit tutustua demoomme osoitteessa",
    paymentMethodsQuestion: "Mitä maksutapoja hyväksytte?",
    paymentMethodsAnswer:
      "Hyväksymme kaikki yleisimmät luottokortit, mukaan lukien Visa, Mastercard ja American Express. Ota yhteyttä muita maksutapoja varten.",
    cancelSubscriptionQuestion: "Voinko peruuttaa tilaukseni milloin tahansa?",
    cancelSubscriptionAnswer:
      "Kyllä, voit peruuttaa tilauksesi milloin tahansa. Pääsysi jatkuu nykyisen laskutuskauden loppuun asti.",
    readyToRevolutionize: "Valmis mullistamaan ravintolasi toiminnan?",
    contactSales: "Ota yhteyttä myyntiin",

    // Contact page
    getInTouch: "Ota Yhteyttä",
    contactHeroDescription:
      "Onko sinulla kysyttävää ByteBellystä? Olemme täällä auttaaksemme sinua löytämään täydellisen ratkaisun ravintolallesi.",
    contactInformation: "Yhteystiedot",
    contactTeamDescription:
      "Tiimimme on valmis vastaamaan kysymyksiisi ja auttamaan sinua pääsemään alkuun ByteBellyn ravintolahallinnan alustan kanssa.",
    emailUs: "Lähetä Sähköpostia",
    callUs: "Soita Meille",
    businessHours: "Joka päivä, 24/7",
    visitUs: "Vieraile Luonamme",
    ourWebsites: "Verkkosivustomme",
    demoLink: "Demo",
    sendUsMessage: "Lähetä Meille Viesti",
    fullName: "Koko Nimi",
    emailAddress: "Sähköpostiosoite",
    phoneNumber: "Puhelinnumero",
    companyName: "Yrityksen Nimi",
    interestedIn: "Kiinnostunut",
    message: "Viesti",
    yourName: "Nimesi",
    emailPlaceholder: "sinun@email.com",
    phonePlaceholder: "(123) 456-7890",
    companyPlaceholder: "Yrityksesi",
    generalInquiry: "Yleinen Tiedustelu",
    requestDemo: "Pyydä Demo",
    pricingInfo: "Hintatiedot",
    technicalSupport: "Tekninen Tuki",
    messagePlaceholder: "Kuinka voimme auttaa sinua?",
    sending: "Lähetetään",
    sendMessage: "Lähetä Viesti",
    thankYou: "Kiitos!",
    messageSuccessText:
      "Viestisi on lähetetty onnistuneesti. Palaamme asiaan mahdollisimman pian.",
    sendAnotherMessage: "Lähetä Toinen Viesti",
    findUs: "Löydä Meidät",
    visitOurOffice:
      "Vieraile toimistollamme Konyassa, Turkissa. Haluaisimme tavata sinut henkilökohtaisesti ja keskustella siitä, kuinka ByteBelly voi auttaa ravintolaasi.",

    // Device showcase section
    experienceOnAllDevices: "Koe ByteBelly Kaikilla Laitteilla",
    responsiveDesignDescription:
      "Alustamme toimii saumattomasti sekä tietokoneella että mobiililaitteilla, antaen sinulle ja asiakkaillesi joustavuuden hallita ja tilata mistä tahansa.",
    availableOnAllPlatforms:
      "Saatavilla iOS:lle, Androidille ja verkkoselaimille",
    downloadApp: "Lataa Sovellus",

    // Powered By
    poweredBy: "Teknologia",
    mobileAppDevelopment: "Mobiilisovelluskehitys",
    realtimeBackend: "Reaaliaikainen taustajärjestelmä",
    cloudServices: "Pilvipalvelut",
    scalableInfrastructure: "Skaalautuva infrastruktuuri",
    trustedByRestaurants:
      "Luotettu yli 10 ravintolassa maailmanlaajuisesti, yli 100+ päivittäistä tilausta",

    // New translations
    footerCompanyDescription:
      "Mullistamme ruuan toimituskokemuksen innovatiivisella mobiilisovelluksellamme ja ravintolanhallintaratkaisullamme.",
    officeLocation: "Konya, Turkki",
    firstMonthFree: "Ensimmäinen kuukausi ilmainen tilauksella",
    freeTrialTitle: "Kokeile ByteBellyä ilmaiseksi 30 päivän ajan",
    freeTrialDescription:
      "Koe kaikki ByteBellyn ominaisuudet ilman sitoumuksia tai luottokorttia. Saat täyden pääsyn kaikkiin ominaisuuksiin 30 päiväksi.",
    startFreeTrial: "Aloita Ilmainen Kokeilu",
    days: "päivää",
    fullAccess: "Täysi pääsy kaikkiin ominaisuuksiin",
    noCommitment: "Ei sitoutumisvaatimuksia",
    noCreditCard: "Luottokorttia ei tarvita",
    cancelAnytime: "Peruuta milloin tahansa",
    recommended: "SUOSITELTU",
    perfectFor:
      "Täydellinen ravintoloille, jotka haluavat kokeilla ennen sitoutumista",
    noPaymentRequired: "Maksutietoja ei vaadita aloittamiseen",
    contactViaWhatsApp: "Ota yhteyttä WhatsAppilla",

    // New appointment booking translations
    bookAppointment: "Varaa Tapaaminen",
    scheduleDescription:
      "Varaa tapaaminen tiimimme kanssa keskustellaksesi projektistasi.",
    videoConsultation: "Videokonsultaatio",
    demoSession: "Tuote-esittely",
    minutes30: "30 minuuttia",
    minutes45: "45 minuuttia",
    scheduleNow: "Varaa Nyt",
  },

  tr: {
    // Navbar
    home: "Ana Sayfa",
    features: "Özellikler",
    pricing: "Fiyatlandırma",
    contact: "İletişim",
    tryDemo: "Demo Deneyin",

    // Homepage
    heroTitle: "Restoranınız İçin Modern Bir Deneyim",
    heroDescription:
      "ByteBelly - Restoranların siparişleri, teslimatları ve müşteri etkileşimlerini tek bir platformdan yönetmeleri için eksiksiz bir çözüm. Aylık veya yıllık fiyatlandırma seçenekleriyle.",
    getStarted: "Hemen Başlayın",
    tryDemoButton: "Demo Deneyin",

    // Features
    featuresHeroTitle: "ByteBelly: Modern Restoranlar İçin Güçlü Özellikler",
    featuresHeroDescription:
      "ByteBelly'nin kapsamlı araç setinin restoran operasyonlarınızı nasıl düzenleyebileceğini ve müşteri deneyimini nasıl geliştirebileceğini keşfedin.",
    customerMobileApp: "Müşteri Mobil Uygulaması",
    customerAppDescription:
      "Müşterilerinize ByteBelly'nin özellik açısından zengin mobil uygulamasıyla kusursuz bir yemek deneyimi sunun.",
    intuitiveMenuBrowsing: "Sezgisel Menü Tarama",
    intuitiveMenuBrowsingDesc:
      "Ayrıntılı açıklamalar, içerikler ve beslenme bilgileri içeren güzel, görsel açıdan zengin menüler.",
    securePaymentOptions: "Güvenli Ödeme Seçenekleri",
    securePaymentOptionsDesc:
      "Sorunsuz bir ödeme deneyimi için kredi kartları ve dijital cüzdanlar dahil birden çok ödeme yöntemi.",
    realTimeOrderTracking: "Gerçek Zamanlı Sipariş Takibi",
    realTimeOrderTrackingDesc:
      "Doğru tahmini varış süreleri ve sipariş durumu güncellemeleriyle teslimat ilerlemesini gerçek zamanlı olarak takip edin.",
    specialDiscounts: "Özel İndirimler",
    specialDiscountsDesc:
      "İndirim kodları uygulayın ve kategoriler veya tek tek ürünler için özel teklifleri görüntüleyin.",
    pushNotifications: "Anlık Bildirimler",
    pushNotificationsDesc:
      "Sipariş durumunuz, özel promosyonlar ve daha fazlası hakkında gerçek zamanlı güncellemeler alın.",
    lightDarkThemes: "Açık ve Koyu Temalar",
    lightDarkThemesDesc:
      "Gündüz veya gece rahat gezinme için açık ve koyu temalar arasında seçim yapın.",
    multiLanguageSupport: "Çoklu Dil Desteği",
    multiLanguageSupportDesc:
      "Uygulamayı Türkçe, Fince ve İngilizce desteği ile tercih ettiğiniz dilde kullanın.",
    inAppMessaging: "Uygulama İçi Mesajlaşma",
    inAppMessagingDesc:
      "Özel istekler veya sorular için doğrudan uygulama üzerinden restoran personeliyle iletişim kurun.",

    restaurantAdminDashboard: "Restoran Yönetici Paneli",
    restaurantAdminDashboardDesc:
      "ByteBelly'nin, restoran operasyonlarınızı verimli bir şekilde yönetmenize ve veri odaklı kararlar almanıza olanak tanıyan güçlü mobil yönetici uygulaması.",

    // Features section
    allInOneTitle: "Restoran Yönetimi İçin Hepsi Bir Arada Çözüm",
    allInOneDescription:
      "ByteBelly, menü öğelerinden siparişlere, teslimatlardan müşteri katılımına kadar her şeyi kapsamlı bir platformda yönetmenize yardımcı olur.",

    // Features section
    mobileOrdering: "Mobil Sipariş",
    mobileOrderingDesc:
      "Müşterilerin menünüzü akıllı telefonlarından sezgisel mobil uygulamamız ile görüntülemelerine ve sipariş vermelerine olanak tanıyın.",
    adminApp: "Admin Mobil Uygulaması",
    adminAppDesc:
      "Gerçek zamanlı sipariş ve ürün yönetimi için özel mobil yönetici uygulamamızla restoranınızı hareket halindeyken yönetin.",
    orderTracking: "Sipariş Takibi",
    orderTrackingDesc:
      "Hem müşteriler hem de restoran personeli için anlık bildirimlerle sipariş durumunu gerçek zamanlı olarak takip edin.",
    discountSystem: "İndirim Sistemi",
    discountSystemDesc:
      "Satışları artırmak için kategorilere veya tek tek ürünlere indirim kodları ve özel teklifler oluşturun ve yönetin.",
    darkMode: "Açık & Koyu Mod",
    darkModeDesc:
      "Hem müşteri hem de yönetici uygulamaları, gündüz veya gece rahat kullanım için açık ve koyu temaları destekler.",
    multilanguage: "Çoklu Dil Desteği",
    multilanguageDesc:
      "Farklı müşterilere hizmet vermek için Türkçe, Fince ve İngilizce dahil olmak üzere birden fazla dile destek.",

    // Demo section
    tryLiveDemo: "ByteBelly Canlı Demosunu Deneyin",
    liveDemoDesc:
      "ByteBelly'yi canlı demomuzla deneyimleyin. Müşteri arayüzünü keşfedin, sipariş vermeyi deneyin ve platformumuzun restoran işletmenizi nasıl dönüştürebileceğini görün.",
    visitDemoSite: "Demo Sitesini Ziyaret Et",

    // CTA section
    transformRestaurant: "Restoranınızı dönüştürmeye hazır mısınız?",
    joinRestaurants:
      "Türkiye ve dünya genelinde faaliyetlerini optimize etmek ve müşterilerini memnun etmek için ByteBelly kullanan restoranlara katılın.",
    viewPricing: "Fiyatları Görüntüle",
    contactUs: "Bize Ulaşın",

    // Footer
    quickLinks: "Hızlı Bağlantılar",
    legal: "Yasal",
    termsOfService: "Kullanım Koşulları",
    privacyPolicy: "Gizlilik Politikası",
    cookiePolicy: "Çerez Politikası",
    contactUsFooter: "Bize Ulaşın",
    allRightsReserved: "Tüm hakları saklıdır",

    // Pricing page
    pricingTitle: "ByteBelly Fiyatlandırma",
    pricingDescription:
      "Restoranınız için en uygun ödeme seçeneğini seçin. Tüm özellikler dahil, gizli ücret yok.",
    monthly: "Aylık",
    yearly: "Yıllık",
    save20Percent: "%20 Tasarruf",
    plan: "Plan",
    year: "yıl",
    month: "ay",
    billedMonthly: "aylık faturalandırma",
    save: "Tasarruf",
    restaurantMobileAdmin: "Restoran mobil yönetici uygulaması",
    customerMobileOrdering: "Müşteri mobil sipariş uygulaması",
    orderTrackingSystem: "Sipariş takip sistemi",
    categoryManagement: "Kategori yönetimi",
    discountCodes: "İndirim kodları ve özel teklifler",
    lightDarkTheme: "Açık/koyu tema desteği",
    unlimitedOrders: "Sınırsız sipariş",
    unlimitedMenuItems: "Sınırsız menü öğesi",
    freeTechnicalSupport: "Ücretsiz teknik destek",
    yearlyDescription:
      "Aylık faturalandırmaya kıyasla önemli tasarruf sağlayan yıllık abonelik.",
    monthlyDescription: "Uzun vadeli taahhüt olmadan esnek aylık abonelik.",
    frequentlyAskedQuestions: "Sıkça Sorulan Sorular",
    faqDescription:
      "Sorularınız mı var? Yardım etmek için buradayız. Aradığınızı bulamıyorsanız, lütfen bizimle iletişime geçin.",
    whatsIncludedQuestion: "Her iki planda da neler dahil?",
    whatsIncludedAnswer:
      "Hem aylık hem de yıllık planlar aynı özellikleri içerir: yönetici uygulaması, müşteri uygulaması, çoklu dil desteği, sipariş yönetimi ve diğer tüm ByteBelly özellikleri.",
    demoQuestion: "Demo sunuyor musunuz?",
    demoAnswer: "Evet, demomuzu şu adresten inceleyebilirsiniz:",
    paymentMethodsQuestion: "Hangi ödeme yöntemlerini kabul ediyorsunuz?",
    paymentMethodsAnswer:
      "Visa, Mastercard ve American Express dahil tüm büyük kredi kartlarını kabul ediyoruz. Diğer ödeme yöntemleri için bizimle iletişime geçin.",
    cancelSubscriptionQuestion:
      "Aboneliğimi istediğim zaman iptal edebilir miyim?",
    cancelSubscriptionAnswer:
      "Evet, aboneliğinizi istediğiniz zaman iptal edebilirsiniz. Erişiminiz mevcut faturalandırma döneminin sonuna kadar devam edecektir.",
    readyToRevolutionize: "Restoranınızı devrim yaratmaya hazır mısınız?",
    contactSales: "Satış Ekibiyle İletişime Geçin",

    // Contact page
    getInTouch: "İletişime Geçin",
    contactHeroDescription:
      "ByteBelly hakkında sorularınız mı var? Size restoranınız için mükemmel çözümü bulmanıza yardımcı olmak için buradayız.",
    contactInformation: "İletişim Bilgileri",
    contactTeamDescription:
      "Ekibimiz, sorularınızı yanıtlamaya ve restoran yönetim platformu ByteBelly ile başlamanıza yardımcı olmaya hazır.",
    emailUs: "Bize E-posta Gönderin",
    callUs: "Bizi Arayın",
    businessHours: "Hergün, 24/7",
    visitUs: "Bizi Ziyaret Edin",
    ourWebsites: "Web Sitelerimiz",
    demoLink: "Demo",
    sendUsMessage: "Bize Mesaj Gönderin",
    fullName: "Ad Soyad",
    emailAddress: "E-posta Adresi",
    phoneNumber: "Telefon Numarası",
    companyName: "Şirket Adı",
    interestedIn: "İlgilendiğiniz",
    message: "Mesaj",
    yourName: "Adınız",
    emailPlaceholder: "sizin@email.com",
    phonePlaceholder: "(123) 456-7890",
    companyPlaceholder: "Şirketiniz",
    generalInquiry: "Genel Sorgu",
    requestDemo: "Demo Talebi",
    pricingInfo: "Fiyatlandırma Bilgisi",
    technicalSupport: "Teknik Destek",
    messagePlaceholder: "Size nasıl yardımcı olabiliriz?",
    sending: "Gönderiliyor",
    sendMessage: "Mesaj Gönder",
    thankYou: "Teşekkürler!",
    messageSuccessText:
      "Mesajınız başarıyla gönderildi. En kısa sürede size geri döneceğiz.",
    sendAnotherMessage: "Başka Mesaj Gönder",
    findUs: "Bizi Bulun",
    visitOurOffice:
      "Türkiye, Konya'daki ofisimizi ziyaret edin. Sizinle şahsen tanışmak ve ByteBelly'nin restoranınıza nasıl yardımcı olabileceğini tartışmak isteriz.",

    // Device showcase section
    experienceOnAllDevices: "ByteBelly'yi Tüm Cihazlarda Deneyimleyin",
    responsiveDesignDescription:
      "Platformumuz masaüstü ve mobil cihazlarda sorunsuz çalışır, size ve müşterilerinize her yerden yönetme ve sipariş verme esnekliği sağlar.",
    availableOnAllPlatforms:
      "iOS, Android ve Web tarayıcılarında kullanılabilir",
    downloadApp: "Uygulamayı İndir",

    // Powered By
    poweredBy: "Teknoloji",
    mobileAppDevelopment: "Mobil Uygulama Geliştirme",
    realtimeBackend: "Gerçek Zamanlı Backend",
    cloudServices: "Bulut Hizmetleri",
    scalableInfrastructure: "Ölçeklenebilir Altyapı",
    trustedByRestaurants:
      "Dünya çapında 10+ restoran tarafından güvenilen, günlük 100+ sipariş",

    // New translations
    footerCompanyDescription:
      "Yenilikçi mobil uygulamamız ve restoran yönetim çözümümüzle yemek teslimat deneyimini devrimleştiriyoruz.",
    officeLocation: "Konya, Türkiye",
    firstMonthFree: "Abonelikle ilk ay ücretsiz",
    freeTrialTitle: "ByteBelly'yi 30 gün ücretsiz deneyin",
    freeTrialDescription:
      "Hiçbir yükümlülük veya kredi kartı olmadan tüm ByteBelly özelliklerini deneyimleyin. 30 gün boyunca tüm özelliklere tam erişim elde edin.",
    startFreeTrial: "Ücretsiz Denemeyi Başlat",
    days: "gün",
    fullAccess: "Tüm özelliklere tam erişim",
    noCommitment: "Taahhüt gerekmez",
    noCreditCard: "Kredi kartı gerekmez",
    cancelAnytime: "İstediğiniz zaman iptal edin",
    recommended: "ÖNERİLEN",
    perfectFor:
      "Taahhüt etmeden önce denemek isteyen restoranlar için mükemmel",
    noPaymentRequired: "Başlamak için ödeme bilgisi gerekmez",
    contactViaWhatsApp: "WhatsApp ile İletişim",

    // New appointment booking translations
    bookAppointment: "Randevu Al",
    scheduleDescription:
      "Projenizi görüşmek için ekibimizle bir toplantı planlayın.",
    videoConsultation: "Video Danışma",
    demoSession: "Ürün Demosu",
    minutes30: "30 dakika",
    minutes45: "45 dakika",
    scheduleNow: "Şimdi Planla",
  },
};

// Create context
interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

const defaultValue: LanguageContextType = {
  language: "en",
  setLanguage: () => {},
  t: () => "",
};

const LanguageContext = createContext<LanguageContextType>(defaultValue);

// Create provider
interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>("en");

  // Translation function
  const t = (key: string): string => {
    if (!translations[language]) {
      return key;
    }

    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Create hook for easy usage
export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;
